import { useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  useVideoEvent, useVideoFeed,
  extractVideoUrl, extractThumbnail, extractTitle,
  extractDuration, extractTags, extractIsNsfw, formatDuration, formatTimeAgo,
} from '@/hooks/useVideoFeed';
import { useVideoThumbnail } from '@/hooks/useVideoThumbnail';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useDeleteVideo } from '@/hooks/useDeleteVideo';
import { Layout } from '@/components/Layout';
import { UploadModal } from '@/components/UploadModal';
import { VideoCard, VideoCardSkeleton } from '@/components/VideoCard';
import { YouTubeEmbed, YouTubeIcon } from '@/components/YouTubeEmbed';
import { extractYouTubeVideoId, isYouTubeUrl } from '@/lib/youtube';
import { useQueryClient } from '@tanstack/react-query';
import { ZapButton } from '@/components/ZapButton';
import { ZapDialog } from '@/components/ZapDialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/useToast';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import type { Event as NostrToolsEvent } from 'nostr-tools';
import { useOrientationFullscreen } from '@/hooks/useOrientationFullscreen';

function useVideoComments(eventId: string) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['video-comments', eventId],
    queryFn: async (ctx) => {
      const events = await nostr.query(
        [{ kinds: [1111, 1], '#e': [eventId], limit: 50 }],
        { signal: ctx.signal },
      );
      return events.sort((a, b) => a.created_at - b.created_at);
    },
    staleTime: 30000,
  });
}

function useVideoReactions(event: NostrEvent | null | undefined) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['video-reactions', event?.id ?? ''],
    queryFn: async (ctx) => {
      if (!event) return [];

      // Addressable events (kind 34235) are referenced by both their event id
      // AND their coordinate tag `a` — query both and merge.
      const isAddressable = event.kind >= 30000 && event.kind < 40000;

      if (isAddressable) {
        const dTag = event.tags.find(([t]) => t === 'd')?.[1] ?? '';
        const coord = `${event.kind}:${event.pubkey}:${dTag}`;
        const [byId, byCoord] = await Promise.all([
          nostr.query([{ kinds: [7], '#e': [event.id], limit: 200 }], { signal: ctx.signal }),
          nostr.query([{ kinds: [7], '#a': [coord], limit: 200 }], { signal: ctx.signal }),
        ]);
        // Merge and deduplicate by event id
        const seen = new Set<string>();
        return [...byId, ...byCoord].filter(e => {
          if (seen.has(e.id)) return false;
          seen.add(e.id);
          return true;
        });
      }

      return nostr.query(
        [{ kinds: [7], '#e': [event.id], limit: 200 }],
        { signal: ctx.signal },
      );
    },
    staleTime: 30000,
    enabled: !!event?.id,
  });
}

function CommentItem({ event }: { event: NostrEvent }) {
  const { user } = useCurrentUser();
  const author = useAuthor(event.pubkey);
  const meta = author.data?.metadata;
  const name = meta?.name ?? event.pubkey.slice(0, 8) + '…';

  const profileEvent = author.data?.event as NostrToolsEvent | undefined;
  const canZap = !!user &&
    user.pubkey !== event.pubkey &&
    !!profileEvent &&
    !!(meta?.lud16 || meta?.lud06);

  return (
    <div className="group flex gap-3 py-1 rounded-lg px-2 -mx-2 transition-colors hover:bg-black/5 dark:hover:bg-white/5">
      <Link to={`/profile/${event.pubkey}`} className="shrink-0">
        <Avatar className="h-8 w-8">
          <AvatarImage src={meta?.picture} />
          <AvatarFallback className="text-xs font-bold" style={{ background: 'var(--bg-secondary)', color: 'var(--gb-accent)' }}>
            {name[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <Link to={`/profile/${event.pubkey}`} className="text-sm font-semibold no-underline hover:underline truncate" style={{ color: 'var(--text-primary)' }}>
            {name}
          </Link>
          <span className="text-xs shrink-0" style={{ color: 'var(--text-secondary)' }}>{formatTimeAgo(event.created_at)}</span>
        </div>
        <p className="text-sm mt-0.5 whitespace-pre-wrap break-words" style={{ color: 'var(--text-primary)' }}>{event.content}</p>
      </div>
      {canZap && profileEvent && (
        <ZapDialog target={profileEvent}>
          <button
            type="button"
            aria-label={`Zap ${name}`}
            className="shrink-0 self-center opacity-100 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex items-center gap-0.5 px-2 py-1 rounded-full text-xs font-semibold"
            style={{ color: '#f7931a', background: 'rgba(247,147,26,0.12)' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 2v11h3v9l7-12h-4l4-8z" />
            </svg>
            Zap
          </button>
        </ZapDialog>
      )}
    </div>
  );
}

export default function VideoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { mutate: publishEvent } = useNostrPublish();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [showDesc, setShowDesc] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [nsfwConfirmed, setNsfwConfirmed] = useState(false);

  const { mutate: deleteVideo, isPending: isDeleting } = useDeleteVideo();

  const videoRef = useRef<HTMLVideoElement>(null);
  useOrientationFullscreen(videoRef);

  const queryClient = useQueryClient();
  const { data: event, isLoading } = useVideoEvent(id ?? '');
  const { data: relatedVideos } = useVideoFeed();
  const { data: comments, refetch: refetchComments } = useVideoComments(id ?? '');
  const { data: reactions, refetch: refetchReactions } = useVideoReactions(event);

  const author = useAuthor(event?.pubkey ?? '');
  const authorMeta = author.data?.metadata;

  const title = event ? extractTitle(event) : 'Loading...';
  const videoUrl = event ? extractVideoUrl(event) : undefined;
  const storedThumbnail = event ? extractThumbnail(event) : undefined;
  const thumbnail = useVideoThumbnail(event?.id ?? '', storedThumbnail, videoUrl);
  const duration = event ? extractDuration(event) : undefined;
  const isNsfw = event ? extractIsNsfw(event) : false;
  const tags = event ? extractTags(event) : [];
  const description = event?.content ?? '';
  const timeAgo = event ? formatTimeAgo(event.created_at) : '';
  const authorName = authorMeta?.name ?? authorMeta?.display_name ?? event?.pubkey?.slice(0, 8) + '…';
  const isYouTubeVideo = videoUrl ? isYouTubeUrl(videoUrl) : false;
  const youTubeVideoId = videoUrl ? extractYouTubeVideoId(videoUrl) : null;

  useSeoMeta({
    title: title + ' – Goblinbox',
    description: description || `Watch "${title}" on Goblinbox, the decentralized video platform`,
  });

  // A "positive" reaction: +, heart, shaka, thumbs-up, fire, sparkles, or the literal "+"
  const POSITIVE_REACTIONS = new Set(['+', '❤️', '🤙', '👍', '🔥', '✨', '😍', '🙌']);
  const isPositiveReaction = (content: string) =>
    content === '+' || POSITIVE_REACTIONS.has(content);

  const likesCount = reactions?.filter(r => isPositiveReaction(r.content)).length ?? 0;

  // Whether the current user has already liked this video
  const alreadyLiked = !!user && !!reactions?.some(
    r => r.pubkey === user.pubkey && isPositiveReaction(r.content),
  );

  const handleLike = () => {
    if (!user || !event) return;
    if (alreadyLiked) return; // prevent duplicate likes

    // Build correct NIP-25 tags.
    // For addressable events (kind 34235) include both 'e' (the specific event id)
    // and 'a' (the coordinate), plus a 'k' tag for the target kind.
    const isAddressable = event.kind >= 30000 && event.kind < 40000;
    const dTag = event.tags.find(([t]) => t === 'd')?.[1] ?? '';
    const reactionTags: string[][] = [
      ['e', event.id],
      ['p', event.pubkey],
      ['k', String(event.kind)],
    ];
    if (isAddressable) {
      reactionTags.push(['a', `${event.kind}:${event.pubkey}:${dTag}`]);
    }

    publishEvent(
      { kind: 7, content: '👍', tags: reactionTags },
      {
        onSuccess: () => {
          toast({ title: 'Liked!' });
          // Invalidate reactions cache so the count updates
          queryClient.invalidateQueries({ queryKey: ['video-reactions', event.id] });
          refetchReactions();
        },
      },
    );
  };

  const handleComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !event || !comment.trim()) return;
    publishEvent(
      { kind: 1111, content: comment.trim(), tags: [['e', event.id], ['p', event.pubkey]] },
      {
        onSuccess: () => {
          setComment('');
          refetchComments();
          toast({ title: 'Comment posted!' });
        },
      },
    );
  };

  if (!id) { navigate('/'); return null; }

  return (
    <Layout onUploadClick={() => setUploadOpen(true)}>
      <div className="px-4 sm:px-6 py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Main column ──────────────────────────────────────── */}
          <div className="flex-1 min-w-0 overflow-hidden">

            {/* Video player */}
            <div className="w-full mb-4">
              <div
                className="w-full rounded-xl overflow-hidden"
                style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}
              >
                {isLoading ? (
                  <div className="absolute inset-0" style={{ background: 'var(--bg-secondary)' }} />
                ) : isNsfw && !nsfwConfirmed ? (
                  /* NSFW gate — blurred poster with confirmation prompt */
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
                    {thumbnail && (
                      <img
                        src={thumbnail}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ filter: 'blur(24px)', transform: 'scale(1.1)', opacity: 0.6 }}
                      />
                    )}
                    <div className="relative z-10 flex flex-col items-center gap-3">
                      <span className="text-5xl">🔞</span>
                      <p className="text-white font-bold text-lg">Sensitive Content</p>
                      <p className="text-white/80 text-sm max-w-xs">
                        This video has been marked as NSFW or contains a content warning.
                      </p>
                      <button
                        onClick={() => setNsfwConfirmed(true)}
                        className="mt-1 px-6 py-2.5 rounded-full text-sm font-bold text-white transition-opacity hover:opacity-90"
                        style={{ background: 'var(--gb-accent)' }}
                      >
                        I understand, show video
                      </button>
                    </div>
                  </div>
                ) : isYouTubeVideo && youTubeVideoId ? (
                  /* YouTube bridge — render an embedded YouTube iframe */
                  <div className="absolute inset-0">
                    <YouTubeEmbed
                      videoId={youTubeVideoId}
                      originalUrl={videoUrl}
                      fillParent
                    />
                  </div>
                ) : videoUrl ? (
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    poster={thumbnail ?? undefined}
                    controls
                    playsInline
                    className="absolute inset-0 w-full h-full"
                    style={{ objectFit: 'contain', background: '#000' }}
                    preload="metadata"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                    <div className="text-center">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" className="mx-auto mb-2 opacity-40">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      <p className="text-sm">Video not available</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            {isLoading
              ? <Skeleton className="h-7 w-3/4 mb-3" style={{ background: 'var(--bg-secondary)' }} />
              : <h1 className="text-lg sm:text-xl font-bold mb-3 leading-snug" style={{ color: 'var(--text-primary)' }}>{title}</h1>
            }

            {/* Duration / tags row */}
            {!isLoading && (
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {duration !== undefined && (
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {formatDuration(duration)}
                  </span>
                )}
                {/* YouTube source badge */}
                {isYouTubeVideo && (
                  <span
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold"
                    style={{ background: '#ff0000', color: '#fff' }}
                  >
                    <YouTubeIcon size={12} />
                    YouTube
                  </span>
                )}
                {tags.map(tag => (
                  <Link
                    key={tag}
                    to={`/tag/${tag}`}
                    className="text-xs px-3 py-1 rounded-full no-underline transition-colors hover:opacity-80"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--gb-accent)' }}
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Action buttons row */}
            {!isLoading && (
              <div className="flex flex-wrap gap-2 pt-3 mb-4" style={{ borderTop: '1px solid var(--gb-border)' }}>
                <button
                  className="gb-action-btn"
                  onClick={handleLike}
                  disabled={!user || alreadyLiked}
                  aria-label={alreadyLiked ? 'Already liked' : 'Like this video'}
                  style={alreadyLiked ? { color: 'var(--gb-accent)', opacity: 1 } : undefined}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={alreadyLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={alreadyLiked ? 0 : 1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
                  </svg>
                  <span>{likesCount > 0 ? likesCount : (alreadyLiked ? 'Liked' : 'Like')}</span>
                </button>

                {/* Zap button */}
                {event && (
                  <ZapButton
                    target={event as unknown as NostrToolsEvent}
                    className="gb-action-btn"
                  />
                )}

                <button
                  className="gb-action-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast({ title: 'Link copied!' });
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z" />
                  </svg>
                  <span>Share</span>
                </button>

                {/* Delete — only shown to the video owner */}
                {user && event && user.pubkey === event.pubkey && (
                  <button
                    className="gb-action-btn"
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={isDeleting}
                    style={{ color: '#f44336' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                    <span>{isDeleting ? 'Deleting…' : 'Delete'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Channel info card */}
            {!isLoading && event && (
              <div
                className="flex items-center gap-4 p-4 rounded-lg mb-4"
                style={{ background: 'var(--bg-secondary)' }}
              >
                <Link to={`/profile/${event.pubkey}`} className="flex items-center gap-3 flex-1 min-w-0 no-underline">
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarImage src={authorMeta?.picture} />
                    <AvatarFallback className="font-bold" style={{ background: 'var(--gb-accent)', color: '#fff' }}>
                      {authorName[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>{authorName}</p>
                    {authorMeta?.nip05 && (
                      <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                        <span style={{ color: '#1da1f2' }}>✓</span> {authorMeta.nip05}
                      </p>
                    )}
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{timeAgo}</p>
                  </div>
                </Link>
              </div>
            )}

            {/* Description */}
            {description && (
              <div
                className="rounded-lg p-4 mb-6"
                style={{ background: 'var(--bg-secondary)' }}
              >
                <p
                  className="text-sm whitespace-pre-wrap break-words"
                  style={{
                    color: 'var(--text-primary)',
                    display: showDesc ? 'block' : '-webkit-box',
                    WebkitLineClamp: showDesc ? undefined : 3,
                    WebkitBoxOrient: showDesc ? undefined : 'vertical',
                    overflow: showDesc ? 'visible' : 'hidden',
                  } as React.CSSProperties}
                >
                  {description}
                </p>
                {description.length > 200 && (
                  <button
                    onClick={() => setShowDesc(!showDesc)}
                    className="text-xs font-semibold mt-2 hover:underline"
                    style={{ color: 'var(--gb-accent)' }}
                  >
                    {showDesc ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            )}

            {/* Comments */}
            <div className="mb-6">
              <h2 className="text-base font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                {comments?.length ?? 0} Comments
              </h2>

              {user ? (
                <form onSubmit={handleComment} className="flex gap-3 mb-6">
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarImage src={authorMeta?.picture} />
                    <AvatarFallback className="text-xs font-bold" style={{ background: 'var(--gb-accent)', color: '#fff' }}>?</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2">
                    <Textarea
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                      placeholder="Add a comment..."
                      rows={2}
                      className="resize-none text-sm"
                      style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
                    />
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setComment('')}
                        style={{ color: 'var(--text-secondary)' }}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={!comment.trim()}
                        style={{ background: 'var(--gb-accent)', color: '#fff', border: 'none' }}
                      >
                        Comment
                      </Button>
                    </div>
                  </div>
                </form>
              ) : (
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  <Link to="/" className="hover:underline" style={{ color: 'var(--gb-accent)' }}>Sign in</Link> to leave a comment.
                </p>
              )}

              <div className="space-y-5">
                {comments?.map(c => <CommentItem key={c.id} event={c} />)}
                {comments?.length === 0 && (
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No comments yet. Be the first!</p>
                )}
              </div>
            </div>
          </div>

          {/* ── Related videos sidebar ───────────────────────────── */}
          <aside className="lg:w-80 xl:w-[360px] shrink-0">
            <h2
              className="text-xs font-bold uppercase tracking-wider mb-3"
              style={{ color: 'var(--text-secondary)' }}
            >
              Related Videos
            </h2>
            <div className="space-y-3">
              {!relatedVideos
                ? Array.from({ length: 6 }).map((_, i) => <VideoCardSkeleton key={i} horizontal />)
                : relatedVideos.filter(v => v.id !== id).slice(0, 10).map(v => (
                    <VideoCard key={v.id} event={v} horizontal />
                  ))
              }
            </div>
          </aside>
        </div>
      </div>
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />

      {/* Delete confirmation dialog */}
      {event && (
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: 'var(--text-primary)' }}>Delete this video?</AlertDialogTitle>
              <AlertDialogDescription style={{ color: 'var(--text-secondary)' }}>
                This publishes a NIP-09 deletion request to all connected relays. The video will be
                removed from Goblinbox immediately. Note that some relays may retain a copy.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  deleteVideo(event, {
                    onSuccess: () => {
                      toast({ title: 'Video deleted' });
                      navigate(`/profile/${event.pubkey}`);
                    },
                    onError: (err) => {
                      toast({ title: 'Delete failed', description: String(err), variant: 'destructive' });
                    },
                  });
                }}
                style={{ background: '#f44336', color: '#fff', border: 'none' }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Layout>
  );
}
